'use client';
import { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { useRouter } from 'next/navigation';
import { contacts } from '@/_config';

const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

const CHANNELS = [
    {
        icon:      'pi pi-whatsapp',
        title:     'WhatsApp',
        desc:      'Fastest response — usually within minutes',
        color:     '#22c55e',
        bg:        'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))',
        border:    'rgba(34,197,94,0.3)',
        badge:     'Fastest',
        badgeBg:   '#f0fdf4',
        badgeColor:'#16a34a',
        type:      'link',
        href:      null, // filled in render
    },
    {
        icon:      'pi pi-envelope',
        title:     'Email Support',
        desc:      'Detailed queries & documentation requests',
        color:     '#6366f1',
        bg:        'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))',
        border:    'rgba(99,102,241,0.3)',
        badge:     null,
        type:      'link',
        href:      null,
    },
    {
        icon:      'pi pi-phone',
        title:     'Call Us',
        desc:      'Mon–Sat · 10 AM – 6 PM IST',
        color:     '#1bb8a7',
        bg:        'linear-gradient(135deg, rgba(27,184,167,0.12), rgba(14,165,233,0.08))',
        border:    'rgba(27,184,167,0.3)',
        badge:     null,
        type:      'link',
        href:      null,
    },
    {
        icon:      'pi pi-send',
        title:     'Contact Form',
        desc:      'Submit a detailed support request',
        color:     '#f59e0b',
        bg:        'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(249,115,22,0.08))',
        border:    'rgba(245,158,11,0.3)',
        badge:     null,
        type:      'nav',
        href:      '/contactus',
    },
];

/* ─── Channel Card ───────────────────────────────────────────────────────────── */
const ChannelCard = ({ channel, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <div
            className="col-12 sm:col-6 lg:col-3 p-2"
            style={{ cursor: 'pointer' }}
            onClick={onClick}
        >
            <div
                className="border-round-xl p-4 h-full flex flex-column gap-3"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background: '#fff',
                    border: `1.5px solid ${hovered ? channel.color : channel.border}`,
                    boxShadow: hovered ? `0 8px 28px ${channel.color}22` : '0 1px 6px rgba(0,0,0,0.05)',
                    transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
                    transition: 'all 0.25s ease',
                }}
            >
                <div className="flex align-items-center gap-3">
                    <div
                        className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                        style={{
                            width: 44, height: 44,
                            background: hovered ? channel.color : `${channel.color}20`,
                            transition: 'background 0.25s',
                            boxShadow: hovered ? `0 4px 14px ${channel.color}40` : 'none',
                        }}
                    >
                        <i
                            className={channel.icon}
                            style={{
                                fontSize: '1.2rem',
                                color: hovered ? '#fff' : channel.color,
                                transition: 'color 0.25s',
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <p className="m-0 font-bold" style={{ fontSize: '0.92rem', color: 'var(--text-color)' }}>
                            {channel.title}
                        </p>
                        {channel.badge && (
                            <span
                                className="font-bold border-round px-2"
                                style={{
                                    fontSize: '0.62rem',
                                    background: channel.badgeBg,
                                    color: channel.badgeColor,
                                    border: `1px solid ${channel.badgeColor}30`,
                                    lineHeight: '1.8',
                                }}
                            >
                                {channel.badge}
                            </span>
                        )}
                    </div>
                </div>
                <p className="m-0 text-600 flex-1" style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                    {channel.desc}
                </p>
                <div
                    className="flex align-items-center gap-1 font-semibold"
                    style={{ fontSize: '0.78rem', color: channel.color }}
                >
                    <span>Get in touch</span>
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
                </div>
            </div>
        </div>
    );
};

/* ─── Track Status Card ──────────────────────────────────────────────────────── */
const TrackStatusCard = () => {
    const router = useRouter();
    const [hovered, setHovered] = useState(false);

    return (
        <div
            className="flex align-items-center justify-content-between gap-4 flex-wrap mt-4 border-round-xl p-4"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: '#fff',
                border: `1.5px solid ${hovered ? 'rgba(11,49,99,0.35)' : 'rgba(11,49,99,0.12)'}`,
                boxShadow: hovered ? '0 8px 28px rgba(11,49,99,0.1)' : '0 1px 6px rgba(0,0,0,0.04)',
                transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                transition: 'all 0.25s ease',
                cursor: 'pointer',
            }}
            onClick={() => router.push('/track-status')}
        >
            <div className="flex align-items-center gap-3">
                <div
                    className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                    style={{
                        width: 46, height: 46,
                        background: hovered
                            ? 'var(--primary-color)'
                            : 'rgba(11,49,99,0.08)',
                        boxShadow: hovered ? '0 4px 14px rgba(11,49,99,0.3)' : 'none',
                        transition: 'all 0.25s ease',
                    }}
                >
                    <i
                        className="pi pi-search"
                        style={{
                            fontSize: '1.15rem',
                            color: hovered ? '#fff' : 'var(--primary-color)',
                            transition: 'color 0.25s',
                        }}
                    />
                </div>
                <div>
                    <p className="m-0 font-bold text-900" style={{ fontSize: '0.95rem' }}>
                        Track Your Order / Repair Status
                    </p>
                    <p className="m-0 text-500" style={{ fontSize: '0.8rem' }}>
                        Enter your order ID or ticket number to get real-time updates
                    </p>
                </div>
            </div>
            <Button
                label="Track Now"
                icon="pi pi-arrow-right"
                iconPos="right"
                style={{
                    background: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '0.65rem',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    padding: '0.6rem 1.4rem',
                    boxShadow: '0 4px 14px rgba(11,49,99,0.3)',
                    flexShrink: 0,
                }}
                onClick={(e) => { e.stopPropagation(); router.push('/track-status'); }}
            />
        </div>
    );
};

/* ─── SupportCTA ─────────────────────────────────────────────────────────────── */
export const SupportCTA = () => {
    const router = useRouter();
    const [authUser, setAuthUser] = useState(null);
    const [mounted,  setMounted]  = useState(false);

    useEffect(() => {
        setAuthUser(getAuthUser());
        setMounted(true);
    }, []);

    const isLoggedIn = !!authUser;

    const handleChannel = (channel) => {
        if (channel.type === 'nav') { router.push(channel.href); return; }
        window.open(channel.href, '_blank');
    };

    const channels = [
        { ...CHANNELS[0], href: contacts.whatsappUrl },
        { ...CHANNELS[1], href: `mailto:${contacts.emailSupport}` },
        { ...CHANNELS[2], href: `tel:${contacts.support}` },
        { ...CHANNELS[3] },
    ];

    return (
        <section className="container" style={{ padding: '3rem 1rem' }}>

            {/* ── Heading ───────────────────────────────────────────────── */}
            <div className="text-center mb-4">
                <h2 className="m-0 mb-2 font-bold" style={{ fontSize: '1.5rem', color: 'var(--primary-color)' }}>
                    Still Need Help?
                </h2>
                <p
                    className="m-0"
                    style={{
                        fontSize: '0.9rem',
                        width: '100%',
                        textAlign: 'center',
                        background: 'var(--primary-color)',
                        color: '#fff',
                        padding: '0.55rem 1.25rem',
                        borderRadius: '0.5rem',
                        lineHeight: 1.6,
                    }}
                >
                    Our support team is here Mon–Sat, 10 AM – 6 PM IST.
                    We typically respond within a few hours.
                </p>
            </div>

            {/* ── Channel cards ─────────────────────────────────────────── */}
            <div className="grid">
                {channels.map((ch) => (
                    <ChannelCard
                        key={ch.title}
                        channel={ch}
                        onClick={() => handleChannel(ch)}
                    />
                ))}
            </div>

            {/* ── Track Order Status CTA ────────────────────────────────── */}
            <TrackStatusCard />

            {/* ── My Orders CTA for logged-in users ─────────────────────── */}
            {mounted && isLoggedIn && (
                <div
                    className="flex align-items-center justify-content-between gap-4 flex-wrap mt-4 border-round-xl p-4"
                    style={{
                        background: 'linear-gradient(135deg, rgba(27,184,167,0.08), rgba(99,102,241,0.08))',
                        border: '1.5px solid rgba(27,184,167,0.25)',
                    }}
                >
                    <div className="flex align-items-center gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                            style={{
                                width: 46, height: 46,
                                background: 'linear-gradient(135deg, #1bb8a7, #6366f1)',
                                boxShadow: '0 4px 14px rgba(27,184,167,0.35)',
                            }}
                        >
                            <i className="pi pi-shopping-bag" style={{ fontSize: '1.15rem', color: '#fff' }} />
                        </div>
                        <div>
                            <p className="m-0 font-bold text-900" style={{ fontSize: '0.95rem' }}>
                                Track your orders
                            </p>
                            <p className="m-0 text-500" style={{ fontSize: '0.8rem' }}>
                                View status, track shipment, or raise an issue for any order
                            </p>
                        </div>
                    </div>
                    <Button
                        label="My Orders"
                        icon="pi pi-shopping-bag"
                        style={{
                            background: 'linear-gradient(135deg, #1bb8a7, #0ea5e9)',
                            border: 'none',
                            borderRadius: '0.65rem',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            padding: '0.6rem 1.4rem',
                            boxShadow: '0 4px 14px rgba(27,184,167,0.35)',
                            flexShrink: 0,
                        }}
                        onClick={() => router.push('/account/orders')}
                    />
                </div>
            )}

            {/* ── Operating hours note ──────────────────────────────────── */}
            <div
                className="flex align-items-center justify-content-center gap-3 mt-4 flex-wrap"
                style={{ opacity: 0.6 }}
            >
                <div className="flex align-items-center gap-1">
                    <i className="pi pi-clock" style={{ fontSize: '0.75rem', color: '#1bb8a7' }} />
                    <span className="text-xs text-600">Mon – Sat · 10 AM – 6 PM IST</span>
                </div>
                <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#9ca3af' }} />
                <div className="flex align-items-center gap-1">
                    <i className="pi pi-map-marker" style={{ fontSize: '0.75rem', color: '#1bb8a7' }} />
                    <span className="text-xs text-600">Greater Noida, India</span>
                </div>
                <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#9ca3af' }} />
                <div className="flex align-items-center gap-1">
                    <i className="pi pi-check-circle" style={{ fontSize: '0.75rem', color: '#10b981' }} />
                    <span className="text-xs text-600">Avg. response &lt; 2 hrs</span>
                </div>
            </div>
        </section>
    );
};
